import React, { useState, useMemo } from 'react';
import { Search, Download, X, ChevronDown, CheckCircle, XCircle, AlertTriangle, User, Calendar } from 'lucide-react';
import { useDefectsData } from '../../../../hooks/domain/useDefectsData';
import { filterTombstoned } from '../../../../sync/tombstone';  // FT-458: hide soft-deleted defects from history
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
import { useCrew } from '../../../../hooks/useCrew';
import { useVessel } from '../../../../contexts/VesselContext';
import FilterTabs from '../../common/FilterTabs';
import Dropdown from '../../../common/Dropdown';
import DefectReportDetailView from './DefectReportDetailView';
// FT-189: Centralized crew name resolution
import { resolveCrewName } from '../crew/utils/crewHelpers';
// FT-352: shared per-row indicator (Camera + count for photos) — mirrors
// FT-349e Maintenance History treatment so the two histories look alike.
// FT-353a: same indicator now also renders a Wrench chip when the defect has
// a linked corrective job (`correctiveJobId`).
import RowStatusIndicator from '../../shared/RowStatusIndicator';

// FT-209-6 pattern: correctiveJobId may be a string OR a legacy object
// (`{ id: '...' }`). Normalize before feeding to the row indicator.
const normalizeCorrectiveJobId = (defect) => {
  const raw = defect?.correctiveJobId;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && typeof raw.id === 'string') return raw.id;
  return null;
};

/**
 * DefectsHistoryView - Full-screen view of all defects history
 *
 * Following 3-row master template:
 * ROW 1: Title + Export + Close
 * ROW 2: Search + Severity + Status + Date Range
 * ROW 3: FilterTabs (All/By Equipment/By Severity) + Count
 */
const DefectsHistoryView = ({ onBack }) => {
  // State for filters
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'byEquipment' | 'bySeverity'
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('12months'); // '30days' | '3months' | '12months' | 'all'
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  // FT-97: Selected defect for detail view
  const [selectedDefect, setSelectedDefect] = useState(null);

  // Data hooks
  // FT-458: history view is display-only — hide soft-deleted (tombstoned) defects.
  const { defects: rawDefects } = useDefectsData();
  const defects = filterTombstoned(rawDefects);
  const { equipment } = useEquipmentRegister();
  const { crew } = useCrew();
  const { getCurrentVessel } = useVessel();

  // Get vessel name for exports
  const vessel = getCurrentVessel();
  const vesselName = vessel?.name || 'Vessel';
  // FT-352: currentVesselId needed by PhotoAttachment in the report view.
  const currentVesselId = vessel?.id || null;

  // FT-352: opening a defect from the history list flags scrollToPhotos so the
  // report view auto-scrolls to the photos section when the row has photos.
  // Mirrors FT-349e's MaintenanceHistoryView → MaintenanceCompletionReport wiring.
  const handleSelectDefect = (defect) => {
    const hasPhotos = Array.isArray(defect?.photos) && defect.photos.length > 0;
    setSelectedDefect({ ...defect, scrollToPhotos: hasPhotos });
  };

  // Create equipment lookup map
  const equipmentById = useMemo(() => {
    const map = new Map();
    (equipment || []).forEach(eq => {
      map.set(eq.id, eq);
    });
    return map;
  }, [equipment]);

  // FT-189: Use centralized crew name resolution helper
  const getCrewDisplayName = (crewId) => {
    if (!crewId) return 'Not assigned';
    return resolveCrewName(crewId, crew || []);
  };

  // Helper to get equipment name from ID
  const getEquipmentName = (equipmentId) => {
    if (!equipmentId) return 'Not specified';
    const eq = equipmentById.get(equipmentId);
    return eq?.name || eq?.label || equipmentId;
  };

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const date = typeof dateValue === 'number' ? new Date(dateValue) : new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    const colors = {
      'Critical': { bg: 'bg-red-100', text: 'text-red-700' },
      'High': { bg: 'bg-orange-100', text: 'text-orange-700' },
      'Medium': { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      'Low': { bg: 'bg-blue-100', text: 'text-blue-700' }
    };
    return colors[severity] || { bg: 'bg-gray-100', text: 'text-gray-700' };
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'open' || statusLower === 'Open') {
      return { bg: 'bg-orange-100', text: 'text-orange-700' };
    }
    if (statusLower === 'in progress' || statusLower === 'in_progress') {
      return { bg: 'bg-blue-100', text: 'text-blue-700' };
    }
    if (statusLower === 'resolved' || statusLower === 'closed' || statusLower === 'Closed') {
      return { bg: 'bg-green-100', text: 'text-green-700' };
    }
    return { bg: 'bg-gray-100', text: 'text-gray-700' };
  };

  // Filter defects by date range
  const filteredByDate = useMemo(() => {
    const now = Date.now();
    const msPerDay = 24 * 60 * 60 * 1000;

    return (defects || []).filter(defect => {
      const defectDate = defect.reportedDate || defect.date || defect.created_at;
      if (!defectDate) return true; // Include if no date

      const recordDate = new Date(defectDate).getTime();
      if (isNaN(recordDate)) return true;

      switch (dateRange) {
        case '30days':
          return (now - recordDate) <= 30 * msPerDay;
        case '3months':
          return (now - recordDate) <= 90 * msPerDay;
        case '12months':
          return (now - recordDate) <= 365 * msPerDay;
        case 'all':
        default:
          return true;
      }
    });
  }, [defects, dateRange]);

  // Filter by severity
  const filteredBySeverity = useMemo(() => {
    if (severityFilter === 'all') return filteredByDate;

    return filteredByDate.filter(defect => {
      const defectSeverity = defect.risk_level || defect.severity || defect.priority;
      return defectSeverity === severityFilter;
    });
  }, [filteredByDate, severityFilter]);

  // Filter by status
  const filteredByStatus = useMemo(() => {
    if (statusFilter === 'all') return filteredBySeverity;

    return filteredBySeverity.filter(defect => {
      const defectStatus = defect.status || 'Open';
      if (statusFilter === 'Open') {
        return defectStatus === 'Open' || defectStatus === 'open';
      }
      if (statusFilter === 'Closed') {
        return defectStatus === 'Closed' || defectStatus === 'closed' || defectStatus === 'resolved' || defectStatus === 'Resolved';
      }
      return defectStatus === statusFilter;
    });
  }, [filteredBySeverity, statusFilter]);

  // Filter by search term
  const filteredBySearch = useMemo(() => {
    if (!searchTerm.trim()) return filteredByStatus;

    const term = searchTerm.toLowerCase();
    return filteredByStatus.filter(defect => {
      // Search in description
      if (defect.description?.toLowerCase().includes(term)) return true;
      // Search in location
      if (defect.location?.toLowerCase().includes(term)) return true;
      // Search in ID
      if (defect.id?.toString().toLowerCase().includes(term)) return true;
      // Search in equipment name
      const eqName = getEquipmentName(defect.equipment_linked || defect.equipmentId);
      if (eqName.toLowerCase().includes(term)) return true;
      // Search in assigned person
      const assignee = getCrewDisplayName(defect.assigned_to);
      if (assignee.toLowerCase().includes(term)) return true;
      return false;
    });
  }, [filteredByStatus, searchTerm]);

  // Sort by date (newest first)
  const sortedRecords = useMemo(() => {
    return [...filteredBySearch].sort((a, b) => {
      const dateA = new Date(a.reportedDate || a.date || a.created_at || 0).getTime();
      const dateB = new Date(b.reportedDate || b.date || b.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [filteredBySearch]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = sortedRecords.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, severityFilter, statusFilter, dateRange, viewMode]);

  // Group records by equipment or severity if needed
  const groupedRecords = useMemo(() => {
    if (viewMode === 'all') return null;

    const groups = new Map();

    sortedRecords.forEach(defect => {
      if (viewMode === 'byEquipment') {
        // Group by equipment
        const eqId = defect.equipment_linked || defect.equipmentId;
        const key = eqId || '_unlinked';
        if (!groups.has(key)) {
          groups.set(key, {
            name: eqId ? getEquipmentName(eqId) : 'No Equipment Linked',
            records: []
          });
        }
        groups.get(key).records.push(defect);
      } else if (viewMode === 'bySeverity') {
        // Group by severity
        const severity = defect.risk_level || defect.severity || defect.priority || 'Unknown';
        if (!groups.has(severity)) {
          groups.set(severity, { name: severity, records: [] });
        }
        groups.get(severity).records.push(defect);
      }
    });

    // Sort by severity order if grouping by severity
    if (viewMode === 'bySeverity') {
      const severityOrder = ['Critical', 'High', 'Medium', 'Low', 'Unknown'];
      const sortedGroups = new Map();
      severityOrder.forEach(sev => {
        if (groups.has(sev)) {
          sortedGroups.set(sev, groups.get(sev));
        }
      });
      return sortedGroups;
    }

    return groups;
  }, [sortedRecords, viewMode]);

  // Calculate counts for tabs
  const recordCount = sortedRecords.length;

  // Export handlers
  const handleExportCSV = () => {
    const headers = ['Date', 'ID', 'Description', 'Equipment', 'Location', 'Severity', 'Status', 'Assigned To'];
    const rows = sortedRecords.map(d => [
      formatDate(d.reportedDate || d.date || d.created_at),
      d.id || '',
      (d.description || '').replace(/"/g, '""'),
      getEquipmentName(d.equipment_linked || d.equipmentId),
      (d.location || '').replace(/"/g, '""'),
      d.risk_level || d.severity || d.priority || '',
      d.status || '',
      getCrewDisplayName(d.assigned_to)
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${vesselName.replace(/\s+/g, '-')}-defects-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    setShowExportDropdown(false);
  };

  const handleExportPDF = () => {
    // Simple PDF export - using basic approach
    const printContent = `
      <html>
        <head>
          <title>Defects History - ${vesselName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f5f5f5; }
            .critical { background-color: #fee2e2; }
            .high { background-color: #ffedd5; }
            .medium { background-color: #fef3c7; }
            .low { background-color: #dbeafe; }
          </style>
        </head>
        <body>
          <h1>Defects History Report</h1>
          <p><strong>Vessel:</strong> ${vesselName}</p>
          <p><strong>Generated:</strong> ${new Date().toLocaleDateString('en-GB')}</p>
          <p><strong>Total Records:</strong> ${sortedRecords.length}</p>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>ID</th>
                <th>Description</th>
                <th>Equipment</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Assigned To</th>
              </tr>
            </thead>
            <tbody>
              ${sortedRecords.map(d => {
                const severity = (d.risk_level || d.severity || d.priority || '').toLowerCase();
                return `
                  <tr class="${severity}">
                    <td>${formatDate(d.reportedDate || d.date || d.created_at)}</td>
                    <td>${d.id || '-'}</td>
                    <td>${d.description || '-'}</td>
                    <td>${getEquipmentName(d.equipment_linked || d.equipmentId)}</td>
                    <td>${d.risk_level || d.severity || d.priority || '-'}</td>
                    <td>${d.status || '-'}</td>
                    <td>${getCrewDisplayName(d.assigned_to)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    setShowExportDropdown(false);
  };

  // Render a mobile card for a defect
  const renderMobileCard = (defect, index) => {
    const defectDate = defect.reportedDate || defect.date || defect.created_at;
    const assignee = getCrewDisplayName(defect.assigned_to);
    const severity = defect.risk_level || defect.severity || defect.priority || 'Unknown';
    const status = defect.status || 'Open';
    const sevColors = getSeverityColor(severity);
    const statusColors = getStatusColor(status);

    return (
      <div
        key={defect.id || index}
        onClick={() => handleSelectDefect(defect)}
        className="border border-gray-200 rounded-lg p-4 bg-white cursor-pointer hover:border-cyan-300 transition-colors"
      >
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">{defect.description || 'No description'}</div>
            <div className="text-sm text-gray-500">
              #{defect.id?.toString().slice(-6) || 'N/A'} • {formatDate(defectDate)}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            {/* FT-352 + FT-353a: photos + corrective-job indicators inline
                alongside severity badge. Same instance renders both chips
                so the layout stays compact. */}
            <RowStatusIndicator
              hasPhotos={(defect.photos?.length || 0) > 0}
              photoCount={defect.photos?.length || 0}
              jobId={normalizeCorrectiveJobId(defect)}
              variant="inline"
              size="sm"
              testIdPrefix={`defect-mobile-${defect.id}`}
            />
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${sevColors.bg} ${sevColors.text}`}>
              {severity}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-gray-600">
            <User className="h-3.5 w-3.5" />
            {assignee}
          </div>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors.bg} ${statusColors.text}`}>
            {status}
          </span>
        </div>
      </div>
    );
  };

  // Render a single defect row
  const renderDefectRow = (defect, index) => {
    const defectDate = defect.reportedDate || defect.date || defect.created_at;
    const assignee = getCrewDisplayName(defect.assigned_to);
    const severity = defect.risk_level || defect.severity || defect.priority || 'Unknown';
    const status = defect.status || 'Open';
    const sevColors = getSeverityColor(severity);
    const statusColors = getStatusColor(status);
    const equipmentName = getEquipmentName(defect.equipment_linked || defect.equipmentId);

    return (
      <tr
        key={defect.id || index}
        onClick={() => handleSelectDefect(defect)}
        className="hover:bg-gray-50 cursor-pointer"
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {formatDate(defectDate)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          #{defect.id?.toString().slice(-6) || 'N/A'}
        </td>
        <td className="px-6 py-4 text-sm text-gray-900">
          <div className="font-medium max-w-xs truncate">{defect.description || 'No description'}</div>
          {defect.location && (
            <div className="text-xs text-gray-500 mt-0.5">{defect.location}</div>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
          {equipmentName}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${sevColors.bg} ${sevColors.text}`}>
            {severity}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors.bg} ${statusColors.text}`}>
            {status}
          </span>
        </td>
        {/* FT-352 + FT-353a: extras column (photos + linked corrective job).
            Same indicator instance so the two chips share layout. */}
        <RowStatusIndicator
          hasPhotos={(defect.photos?.length || 0) > 0}
          photoCount={defect.photos?.length || 0}
          jobId={normalizeCorrectiveJobId(defect)}
          variant="cell"
          size="sm"
          testIdPrefix={`defect-row-${defect.id}`}
        />
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
          {assignee}
        </td>
      </tr>
    );
  };

  // Render grouped view
  const renderGroupedView = () => {
    if (!groupedRecords || groupedRecords.size === 0) {
      return (
        <div className="px-6 py-12 text-center text-gray-500">
          No defects found matching your filters.
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {Array.from(groupedRecords.entries()).map(([key, group]) => {
          const sevColors = viewMode === 'bySeverity' ? getSeverityColor(group.name) : null;

          return (
            <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className={`px-4 md:px-6 py-3 border-b border-gray-200 ${sevColors ? sevColors.bg : 'bg-gray-50'}`}>
                <h3 className={`text-sm font-semibold ${sevColors ? sevColors.text : 'text-gray-900'}`}>
                  {group.name}
                  <span className="ml-2 text-gray-500 font-normal">({group.records.length} defects)</span>
                </h3>
              </div>

              {/* Mobile cards - visible on small screens */}
              <div className="md:hidden p-3 space-y-3">
                {group.records.map((defect, idx) => renderMobileCard(defect, idx))}
              </div>

              {/* Desktop table - hidden on small screens */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[23%]">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Equipment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Severity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Status</th>
                      {/* FT-352: extras column (photos etc.) */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Extras</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[16%]">Assigned To</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {group.records.map((defect, idx) => renderDefectRow(defect, idx))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    if (sortedRecords.length <= itemsPerPage) return null;

    return (
      <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
        {/* Items per page selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Show:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="rounded-md border-gray-300 text-sm px-2 py-1"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={sortedRecords.length}>All</option>
          </select>
          <span className="text-sm text-gray-700">
            Showing {startIndex + 1}-{Math.min(endIndex, sortedRecords.length)} of {sortedRecords.length}
          </span>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>

          {/* Page numbers */}
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                return page === 1 ||
                       page === totalPages ||
                       Math.abs(page - currentPage) <= 2;
              })
              .map((page, idx, arr) => {
                const prevPage = arr[idx - 1];
                const showEllipsis = prevPage && page - prevPage > 1;

                return (
                  <React.Fragment key={page}>
                    {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        currentPage === page
                          ? 'bg-cyan-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Render flat table view
  const renderTableView = () => {
    if (sortedRecords.length === 0) {
      return (
        <div className="px-6 py-12 text-center text-gray-500">
          {(defects || []).length === 0
            ? 'No defects recorded yet.'
            : 'No defects found matching your filters.'}
        </div>
      );
    }

    return (
      <>
        {/* Mobile cards - visible on small screens */}
        <div className="md:hidden space-y-3">
          {paginatedRecords.map((defect, index) => renderMobileCard(defect, index))}
        </div>

        {/* Desktop table - hidden on small screens */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[23%]">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Equipment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Severity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Status</th>
                {/* FT-352: extras column (photos etc.) */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Extras</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[16%]">Assigned To</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRecords.map((defect, index) => renderDefectRow(defect, index))}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        {renderPagination()}
      </>
    );
  };

  // FT-97: If a defect is selected, show the detail view
  if (selectedDefect) {
    return (
      <DefectReportDetailView
        defect={selectedDefect}
        onBack={() => setSelectedDefect(null)}
        currentVesselId={currentVesselId}
      />
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Header - matches Defects tab pattern */}
      <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
        <div className="flex items-center justify-between gap-6">
          {/* Title - Responsive size */}
          <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
            Defect History
          </h3>

          {/* Search Input - inside header */}
          <div className="relative flex-1 max-w-2xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search history..."
              aria-label="Search defect history"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
            />
          </div>

          {/* Action buttons (Export, Close) */}
          <div className="flex items-center gap-2">
            {/* Export dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-slate-700 border border-slate-500 rounded-md hover:bg-slate-800 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                  <button
                    onClick={handleExportCSV}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export as CSV
                    <span className="text-xs text-gray-400 ml-auto">Spreadsheet</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export as PDF
                    <span className="text-xs text-gray-400 ml-auto">Print-ready</span>
                  </button>
                </div>
              )}
            </div>

            {/* Close/Back button */}
            <button
              onClick={onBack}
              className="inline-flex items-center justify-center p-2 text-white hover:text-gray-200 hover:bg-slate-500 rounded-md transition-colors"
              title="Back to Defects"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Section - below header with light background */}
      <div className="px-6 py-5 bg-gray-50 border-b border-gray-200">
        <div className="space-y-3">
          {/* Row 1: Dropdowns (Severity, Status, Date Range) */}
          <div className="flex flex-wrap gap-3">
            <Dropdown
              value={severityFilter}
              onChange={setSeverityFilter}
              width="w-auto min-w-[140px]"
              options={[
                { value: 'all', label: 'All Severities' },
                { value: 'Critical', label: 'Critical' },
                { value: 'High', label: 'High' },
                { value: 'Medium', label: 'Medium' },
                { value: 'Low', label: 'Low' },
              ]}
            />
            <Dropdown
              value={statusFilter}
              onChange={setStatusFilter}
              width="w-auto min-w-[130px]"
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'Open', label: 'Open' },
                { value: 'Closed', label: 'Resolved' },
              ]}
            />
            <Dropdown
              value={dateRange}
              onChange={setDateRange}
              width="w-auto min-w-[150px]"
              options={[
                { value: '30days', label: 'Last 30 days' },
                { value: '3months', label: 'Last 3 months' },
                { value: '12months', label: 'Last 12 months' },
                { value: 'all', label: 'All time' },
              ]}
            />
          </div>

          {/* Row 2: FilterTabs + Count */}
          <div className="flex items-center justify-between">
            <FilterTabs
              tabs={[
                { id: 'all', label: 'All History' },
                { id: 'byEquipment', label: 'By Equipment' },
                { id: 'bySeverity', label: 'By Severity' }
              ]}
              activeTab={viewMode}
              onTabChange={setViewMode}
            />
            <span className="text-sm text-gray-500">{recordCount} defects</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {viewMode === 'all' ? renderTableView() : renderGroupedView()}
      </div>
    </div>
  );
};

export default DefectsHistoryView;
